import { useCallback, useEffect, useMemo, useState } from 'react';
import { Flex } from 'antd';
import { Link } from 'react-router-dom';

import { HrmRequestFormResponse } from '@Types/api/form';
import { RequestListSearchResponse } from '@Types/api/requestList';
import { RequestList } from '@Components/RequestList';
import {
  mapColumns,
  dataSourceMap,
} from '@Features/EmployeeSelfService/requests/LeaveRequest/list';
import useStatusFinderByNum from '@Hooks/useStatusFinderByNum';
import { useGetLeaveRequestsMutation } from '@Features/EmployeeSelfService/requests/LeaveRequest/store/api/leaveRequestApiSlice';

import { useRequestTypeFinderById, useResizeLayout } from '@Hooks/index';

import { SortConfig } from '@Features/types/SortConfig';
import styles from './latest-requests.module.css';
import { REQUESTS_LIMIT, LEAVE_REQUESTS_LINK } from './constants';

const LatestRequests: React.FC = () => {
  const [listRequests, setListRequests] =
    useState<RequestListSearchResponse<HrmRequestFormResponse>>();
  const [requestsDataList, setRequestsDataList] = useState<
    Array<HrmRequestFormResponse>
  >([]);

  const [currentPage, setCurrentPage] = useState(0);
  const { isMobile } = useResizeLayout();

  const { findStatus } = useStatusFinderByNum('Leave');
  const { findRequestType } = useRequestTypeFinderById('Leave');

  const [getLeaveRequests, { isLoading: loading }] =
    useGetLeaveRequestsMutation();

  useEffect(() => {
    if (!isMobile) {
      getLeaveRequests({ size: REQUESTS_LIMIT, sortOrder: 'desc' })
        .unwrap()
        .then((response) => {
          setListRequests(response);
        })
        .catch(() => {});
    }
  }, [getLeaveRequests, isMobile]);

  const dataSource = useMemo(
    () => dataSourceMap(listRequests!, findStatus, findRequestType),
    [listRequests, findStatus, findRequestType]
  );

  const handleLoadMore = (): void => {
    if (loading) {
      return;
    }

    getLeaveRequests({ page: currentPage })
      .unwrap()
      .then((response) => {
        setCurrentPage(currentPage + 1);
        setListRequests(response);
        if (response?.content) {
          setRequestsDataList([...requestsDataList, ...response.content]);
        }
      })
      .catch(() => {});
  };

  const handleSort = useCallback(
    (sortConfig: SortConfig | null) => {
      getLeaveRequests({ ...sortConfig })
        .unwrap()
        .then((response) => {
          setListRequests(response);
        });
    },
    [getLeaveRequests]
  );

  useEffect(() => {
    if (isMobile) {
      handleLoadMore();
    }
  }, [isMobile]);

  return (
    <div className={styles.wrapper}>
      <Flex className={styles.header}>
        <span className={styles.title}>Latest Requests</span>
        <Link to={LEAVE_REQUESTS_LINK}>View All</Link>
      </Flex>
      <RequestList
        onLoadMore={handleLoadMore}
        listRequests={listRequests}
        requestsDataList={requestsDataList}
        mapColumns={mapColumns}
        dataSource={dataSource}
        requestType="Leave"
        showAction={false}
        onSort={handleSort}
        hidePagination
      />
    </div>
  );
};

export default LatestRequests;
